import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './BioEditor.css';

const MAX_BIO_LENGTH = 160;

/**
 * Editable bio field with character counter.
 *
 * Appears as plain text by default, transforms to editable on click.
 * Shows character counter and auto-saves on blur.
 *
 * @param {Object} props
 * @param {string} props.value - Current bio text
 * @param {function} props.onChange - Handler for bio updates
 * @returns {JSX.Element}
 */
function BioEditor({ value, onChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef(null);

  /* Sync local value when prop changes */
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  /* Focus textarea when entering edit mode */
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      /* Place cursor at end */
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= MAX_BIO_LENGTH) {
      setLocalValue(newValue);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setLocalValue(value);
      setIsEditing(false);
    }
  };

  const isEmpty = !localValue.trim();
  const charCount = localValue.length;

  return (
    <div className="bio-editor">
      {isEditing ? (
        <div className="bio-editor__edit-wrapper">
          <textarea
            ref={textareaRef}
            className="bio-editor__textarea"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Write your bio here."
            maxLength={MAX_BIO_LENGTH}
            rows={3}
            aria-label="Bio"
          />
          <span className="bio-editor__counter">
            {charCount}/{MAX_BIO_LENGTH}
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={`bio-editor__display ${isEmpty ? 'bio-editor__display--empty' : ''}`}
          onClick={handleClick}
          aria-label="Edit bio"
        >
          {isEmpty ? 'Write your bio here.' : localValue}
        </button>
      )}
    </div>
  );
}

BioEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default BioEditor;
